import * as Dialog from '@radix-ui/react-dialog';
import { Title, MutedText, CancelBtn } from '../../../account/account-settings.styled';
import { Divider, DescriptionList, BulletItem } from '../../billing.styled';
import {
  Overlay,
  ContentShell,
  DialogPanel,
  PlanCard,
  PlanHeader,
  Badge,
  FeatureListWrap,
  CurrentTrial,
} from './upgrade-now.styled';
import { UpgradeNowLogicProps, fmtCurrency, useUpgradeNow } from './upgrade-now.hooks';
import { OnboardBtn } from '../../../profile/profile.styled';
import { VSpace } from '../../../account/account-settings';
import { RadioButton } from '../../../../../../components/radio-button/radio-button';

type Props = UpgradeNowLogicProps & {
  open: boolean;
  onClose: () => void;
};

export default function UpgradePlanModal({
  open,
  onClose,
  onConfirm,
  trialEndsOn,
  currentPlan,
}: Props) {
  const {
    selectedPlan,
    billingCycle,
    setSelectedPlan,
    setBillingCycle,
    sanctumPricing,
    sanctumSaving,
    northStarPricing,
    northStarSaving,
    annualMonthlyAfterDiscount,
    annualTotal,
    annualSavings,
    monthlyRate,
    trialDateText,
    sanctumFeatures,
    northStarFeatures,
    confirm,
  } = useUpgradeNow({ onConfirm, trialEndsOn, currentPlan });

  const renderFeatureList = (items: string[]) => (
    <FeatureListWrap>
      <DescriptionList>
        {items.map((text, idx) => (
          <BulletItem key={idx}>{text}</BulletItem>
        ))}
      </DescriptionList>
    </FeatureListWrap>
  );

  return (
    <Dialog.Root open={open} onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <Dialog.Portal>
        <Overlay />
        <ContentShell aria-modal="true" role="dialog" aria-labelledby="upgrade-plan-title" aria-describedby={undefined}>
          <Dialog.Title style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
            Upgrade plan
          </Dialog.Title>
          <DialogPanel as="section">
            <VSpace $h={4} />

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Title id="upgrade-plan-title">Subscribe now</Title>
              <VSpace $h={16} />
              <MutedText style={{ fontSize: '14px' }}>
                {`Your trial ends on ${trialDateText}. Confirm your plan to continue seamlessly. You won’t be charged until the trial ends.`}
              </MutedText>

              <VSpace $h={16} />
              <MutedText id="choose-plan-label" style={{ fontSize: '14px', fontWeight: 500 }}>
                Choose your plan
              </MutedText>
              <VSpace $h={16} />

              {/* Plans radiogroup */}
              <div role="radiogroup" aria-labelledby="choose-plan-label">
                {/* Sanctum */}
                <PlanCard
                  style={{ padding: '12px' }}
                  type="button"
                  onClick={() => setSelectedPlan('Sanctum')}
                  $active={selectedPlan === 'Sanctum'}
                  role="radio"
                  aria-checked={selectedPlan === 'Sanctum'}
                  aria-label="Sanctum plan"
                >
                  <CurrentTrial>
                    You’re currently on <br /> the Sanctum trial
                  </CurrentTrial>
                  <VSpace $h={12} />
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <PlanHeader>
                      <RadioButton
                        name="plan"
                        value="Sanctum"
                        checked={selectedPlan === 'Sanctum'}
                        onChange={() => setSelectedPlan('Sanctum')}
                      />
                      <Title style={{ fontSize: '15px' }}>Sanctum</Title>
                    </PlanHeader>
                    {currentPlan === 'Sanctum' && <Badge>Current plan</Badge>}
                  </div>

                  <VSpace $h={12} />
                  <Title>{`${fmtCurrency(sanctumPricing.monthly)}/mo`}</Title>
                  <VSpace $h={4} />
                  <MutedText style={{ fontSize: '14px', lineHeight: 1.4, fontWeight: 400 }}>
                    {`${fmtCurrency(
                      sanctumPricing.annualTotal
                    )} billed at outset of 12-month subscription`}
                  </MutedText>
                  <VSpace $h={4} />
                  <Title style={{ fontSize: '14px', lineHeight: 1.4, color: '#8FB0FF' }}>
                    {`Saving ${fmtCurrency(sanctumSaving)} per annum`}
                  </Title>

                  <VSpace $h={12} />
                  <Divider />

                  {renderFeatureList(sanctumFeatures)}
                </PlanCard>

                <VSpace $h={16} />

                {/* North Star */}
                <PlanCard
                  style={{ padding: '12px' }}
                  type="button"
                  onClick={() => setSelectedPlan('North Star')}
                  $active={selectedPlan === 'North Star'}
                  role="radio"
                  aria-checked={selectedPlan === 'North Star'}
                  aria-label="North Star plan"
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <PlanHeader>
                      <RadioButton
                        name="plan"
                        value="North Star"
                        checked={selectedPlan === 'North Star'}
                        onChange={() => setSelectedPlan('North Star')}
                      />
                      <Title style={{ fontSize: '15px' }}>North Star</Title>
                    </PlanHeader>
                    <Badge
                      style={{
                        border: '1px solid #5E84ED',
                        borderRadius: '12px',
                        color: '#5E84ED',
                        width: '92px',
                        height: '25px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      Best value
                    </Badge>
                  </div>

                  <VSpace $h={12} />
                  <MutedText style={{ fontWeight: 600, fontSize: '14px' }}>
                    Annual pricing
                  </MutedText>
                  <VSpace $h={4} />
                  <Title>{`${fmtCurrency(northStarPricing.monthly)}/mo`}</Title>
                  <VSpace $h={4} />
                  <MutedText style={{ fontSize: '14px', lineHeight: 1.4, fontWeight: 400 }}>
                    {`${fmtCurrency(
                      northStarPricing.annualTotal
                    )} billed at outset of 12-month subscription`}
                  </MutedText>
                  <VSpace $h={4} />
                  <Title style={{ fontSize: '14px', lineHeight: 1.4, color: '#8FB0FF' }}>
                    {`Saving ${fmtCurrency(northStarSaving)} per annum`}
                  </Title>

                  <VSpace $h={12} />
                  <Divider />

                  {renderFeatureList(northStarFeatures)}
                </PlanCard>
              </div>

              <VSpace $h={48} />

              {/* Billing cycle radios */}
              <MutedText id="choose-cycle-label" style={{ fontWeight: 500, fontSize: '14px' }}>
                Choose your billing cycle
              </MutedText>
              <VSpace $h={12} />

              <div role="radiogroup" aria-labelledby="choose-cycle-label">
                <PlanCard
                  type="button"
                  onClick={() => setBillingCycle('Annual')}
                  $active={billingCycle === 'Annual'}
                  role="radio"
                  aria-checked={billingCycle === 'Annual'}
                  aria-label="Annual subscription"
                >
                  <PlanHeader>
                    <RadioButton
                      name="billingCycle"
                      value="Annual"
                      checked={billingCycle === 'Annual'}
                      onChange={() => setBillingCycle('Annual')}
                    />
                    <Title style={{ fontSize: '15px' }}>Annual Subscription</Title>
                  </PlanHeader>
                  <div style={{ paddingLeft: '29px' }}>
                    <VSpace $h={4} />
                    <MutedText>
                      {`${fmtCurrency(annualMonthlyAfterDiscount)} per month (after discount)`}
                    </MutedText>
                    <VSpace $h={4} />
                    <MutedText style={{ lineHeight: '1.4' }}>
                      {`${fmtCurrency(annualTotal)} billed at outset of 12-month subscription`}
                    </MutedText>
                    <VSpace $h={4} />

                    <MutedText
                      style={{
                        fontWeight: '600',
                        color: '#8FB0FF',
                      }}
                    >
                      {`Save ${fmtCurrency(annualSavings)} per annum`}
                    </MutedText>
                  </div>
                </PlanCard>

                <VSpace $h={12} />

                <PlanCard
                  type="button"
                  onClick={() => setBillingCycle('Monthly')}
                  $active={billingCycle === 'Monthly'}
                  role="radio"
                  aria-checked={billingCycle === 'Monthly'}
                  aria-label="Monthly subscription"
                >
                  <PlanHeader>
                    <RadioButton
                      name="billingCycle"
                      value="Monthly"
                      checked={billingCycle === 'Monthly'}
                      onChange={() => setBillingCycle('Monthly')}
                    />
                    <Title style={{ fontSize: '15px' }}>Monthly Subscription</Title>
                  </PlanHeader>
                  <div style={{ paddingLeft: '29px' }}>
                    <VSpace $h={4} />
                    <MutedText>{`${fmtCurrency(monthlyRate)} billed each month`}</MutedText>
                    <VSpace $h={4} />
                    <MutedText style={{ lineHeight: '1.4' }}>
                      Pay as you go - cancel anytime
                    </MutedText>
                  </div>
                </PlanCard>
              </div>

              <VSpace $h={16} />
              <MutedText>
                {`Your trial will end on ${trialDateText}. Billing will start at the ${selectedPlan} rate on your chosen schedule.`}
              </MutedText>
              <VSpace $h={16} />

              <OnboardBtn
                type="button"
                onClick={confirm}
                aria-label={`Confirm ${selectedPlan} plan with ${billingCycle} billing`}
              >
                Confirm Subscription
              </OnboardBtn>

              <VSpace $h={16} />

              <Dialog.Close asChild>
                <CancelBtn type="button" aria-label="Close upgrade modal">
                  Close
                </CancelBtn>
              </Dialog.Close>
            </div>
            <VSpace $h={51} />
          </DialogPanel>
        </ContentShell>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
